import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/components/BrandLogo";
import { ArrowRight, LogIn } from "lucide-react";

// Warehouse racking / mezzanine hero photo — high-quality Unsplash image of a
// steel pallet-rack aisle. Falls back to the second URL if the first is blocked.
const HERO_IMG =
  "https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1600&q=80";
const HERO_IMG_FALLBACK =
  "https://images.pexels.com/photos/1797428/pexels-photo-1797428.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function Landing() {
  const nav = useNavigate();
  const { user } = useAuth();

  const goSignIn = () => {
    if (!user) nav("/login");
    else if (user.status !== "approved") nav("/pending");
    else nav(user.role === "admin" ? "/admin" : "/dashboard");
  };

  return (
    <div
      className="min-h-screen relative overflow-hidden flex flex-col"
      style={{
        background:
          "radial-gradient(1200px 600px at 15% -10%, #f4ede3 0%, transparent 60%), " +
          "radial-gradient(900px 500px at 90% 100%, #eef4ea 0%, transparent 55%), " +
          "linear-gradient(180deg, #f7f4ee 0%, #efeae0 100%)",
      }}
    >
      {/* Faint isometric-grid backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(#111 1px, transparent 1px), linear-gradient(90deg, #111 1px, transparent 1px)",
          backgroundSize: "80px 80px, 80px 80px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 40%, transparent 78%)",
        }}
      />

      {/* Top bar — sign-in right (branding hidden per request) */}
      <header className="relative z-10 px-6 sm:px-10 h-16 flex items-center justify-end">
        <Button
          data-testid="landing-signin"
          size="sm"
          onClick={goSignIn}
          className="rounded-sm h-9 px-4 font-mono text-[11px] uppercase tracking-widest text-white hover:brightness-110"
          style={{ background: BRAND.red }}
        >
          <LogIn className="w-3.5 h-3.5 mr-1.5" />
          Sign in
        </Button>
      </header>

      {/* Hero */}
      <main className="relative z-10 flex-1 flex items-center px-6 sm:px-10 pb-16">
        <div className="max-w-6xl mx-auto w-full grid md:grid-cols-[1.05fr_1fr] gap-10 items-center">
          {/* Left: brand block */}
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-neutral-300/70 bg-white/70 backdrop-blur">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: BRAND.green }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-600">
                Established Storage Manufacturer
              </span>
            </div>

            <h1
              data-testid="landing-heading"
              className="mt-6 font-bold tracking-tight leading-[1.02] text-neutral-900"
              style={{ fontSize: "clamp(2.6rem, 5.5vw, 4.4rem)" }}
            >
              <span style={{ color: BRAND.red }}>Adarsh</span>
              <span> </span>
              <span style={{ color: BRAND.green }}>Udyog</span>
            </h1>

            <p className="mt-3 text-lg sm:text-xl text-neutral-700 max-w-xl leading-relaxed">
              Racking systems, mezzanines, and supermarket fixtures — designed,
              engineered, and installed as one complete storage solution.
            </p>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Button
                data-testid="landing-cta-primary"
                size="lg"
                onClick={goSignIn}
                className="rounded-sm h-11 px-6 font-mono text-xs uppercase tracking-[0.18em] text-white hover:brightness-110 shadow-sm"
                style={{ background: BRAND.red }}
              >
                {user ? "Open workspace" : "Sign in to designer"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">
                Access is provisioned by your administrator
              </div>
            </div>
          </div>

          {/* Right: rack / mezzanine hero photograph */}
          <div className="justify-self-center md:justify-self-end w-full max-w-lg">
            <div className="relative rounded-md overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] border border-neutral-200/70 bg-neutral-100 aspect-[4/5]">
              <img
                data-testid="landing-hero-photo"
                src={HERO_IMG}
                onError={(e) => { if (e.currentTarget.src !== HERO_IMG_FALLBACK) e.currentTarget.src = HERO_IMG_FALLBACK; }}
                alt="Industrial pallet racking and mezzanine storage"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              {/* Subtle gradient overlay for readability of the corner tag */}
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)" }}
              />
              {/* Corner brand ribbons */}
              <div className="absolute -top-2 -left-2 w-12 h-1.5 rounded-sm" style={{ background: BRAND.red }} />
              <div className="absolute -bottom-2 -right-2 w-12 h-1.5 rounded-sm" style={{ background: BRAND.green }} />
              {/* Bottom-left caption */}
              <div className="absolute left-4 bottom-4 text-white">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">// project</div>
                <div className="font-semibold text-lg leading-tight tracking-tight">Multi-tier mezzanine · pallet racking</div>
              </div>
            </div>
            <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500 text-center">
              internal layout designer · warehouse + supermarket
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 px-6 sm:px-10 pb-6 flex items-center justify-between text-neutral-500">
        <div className="font-mono text-[10px] uppercase tracking-widest">
          © {new Date().getFullYear()} Adarsh Udyog
        </div>
        <div className="font-mono text-[10px] uppercase tracking-widest">
          Rack · Mezzanine · Supermarket
        </div>
      </footer>
    </div>
  );
}
